import { CartAddEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class CartUpsellComponent extends HTMLElement {
  /** @type {number} */
  #currentIndex = 0;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.#updateArrows();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  /**
   * Reset carousel to first item when cart updates (morph resets scroll but not our index).
   */
  #handleCartUpdate = () => {
    this.#currentIndex = 0;
    const carousel = this.#carousel;
    if (carousel) carousel.scrollLeft = 0;
    this.#updateArrows();
  };

  /** @returns {HTMLElement | null} */
  get #carousel() {
    return this.querySelector('.cart-upsell__carousel');
  }

  /** @returns {HTMLButtonElement | null} */
  get #prevBtn() {
    return this.querySelector('[data-direction="prev"]');
  }

  /** @returns {HTMLButtonElement | null} */
  get #nextBtn() {
    return this.querySelector('[data-direction="next"]');
  }

  /** @returns {NodeListOf<HTMLElement>} */
  get #items() {
    return this.querySelectorAll('.cart-upsell__item');
  }

  get #totalItems() {
    return this.#items.length;
  }

  /**
   * Delegated click handler.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const arrow = target.closest('[data-direction]');
    if (arrow) {
      event.preventDefault();
      const direction = /** @type {HTMLElement} */ (arrow).dataset.direction;
      if (direction === 'prev') this.#goPrev();
      if (direction === 'next') this.#goNext();
      return;
    }

    const actionEl = target.closest('[data-action]');
    if (actionEl) {
      event.preventDefault();
      const button = /** @type {HTMLButtonElement} */ (actionEl);
      const action = button.dataset.action;

      if (action === 'add-to-cart') {
        this.#addToCart(button);
      } else if (action === 'open-shade') {
        this.#openShadeDrawer(button);
      }
    }
  };

  #goPrev() {
    if (this.#currentIndex > 0) {
      this.#currentIndex--;
      this.#scrollToIndex();
    }
  }

  #goNext() {
    if (this.#currentIndex < this.#totalItems - 1) {
      this.#currentIndex++;
      this.#scrollToIndex();
    }
  }

  #scrollToIndex() {
    const carousel = this.#carousel;
    const item = this.#items[this.#currentIndex];
    if (!carousel || !item) return;

    carousel.scrollTo({
      left: item.offsetLeft - carousel.offsetLeft,
      behavior: 'smooth',
    });

    this.#updateArrows();
  }

  #updateArrows() {
    const prevBtn = this.#prevBtn;
    const nextBtn = this.#nextBtn;
    if (prevBtn) prevBtn.disabled = this.#currentIndex <= 0;
    if (nextBtn) nextBtn.disabled = this.#currentIndex >= this.#totalItems - 1;
  }

  /**
   * @param {HTMLButtonElement} button
   */
  async #addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.disabled) return;

    button.disabled = true;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const cartItems = document.querySelectorAll('cart-items-component');
    /** @type {string[]} */
    const sectionIds = [];
    cartItems.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
    }

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });

      const data = await response.json();

      if (data.status) {
        button.disabled = false;
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'cart-upsell',
          itemCount: 1,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error('Cart upsell add to cart error:', error);
    } finally {
      button.disabled = false;
    }
  }

  /**
   * @param {HTMLButtonElement} button
   */
  async #openShadeDrawer(button) {
    const productUrl = button.dataset.productUrl;
    const productId = button.dataset.productId;
    if (!productUrl) return;

    await customElements.whenDefined('shade-drawer-component');
    const shadeDrawer = /** @type {HTMLElement & { open: Function }} */ (
      document.querySelector('shade-drawer-component')
    );
    if (shadeDrawer) {
      shadeDrawer.open(productUrl, productId);
    }
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
